import { useEffect, useMemo, useRef } from 'react'
import type { Segment } from './types'

/** Warm nearby posters without fetching the entire archive while skimming. */
export function usePosterPreload(segments: Segment[], utc: number) {
  const images = useRef(new Map<string, HTMLImageElement>())
  const next = segments.findIndex((segment) => segment.end > utc)
  const index = next < 0 ? segments.length - 1 : next
  const urls = useMemo(() => {
    const nearby = [0, -1, 1, -2, 2].flatMap((offset) => {
      const url = segments[index + offset]?.recording.posterUrl
      return url ? [url] : []
    })
    return [...new Set(nearby)]
  }, [segments, index])

  useEffect(() => {
    const cache = images.current
    for (const url of urls) {
      const existing = cache.get(url)
      if (existing) {
        // Refresh insertion order so eviction keeps the current neighborhood warm.
        cache.delete(url)
        cache.set(url, existing)
        continue
      }
      // Bound both retained images and outstanding speculative downloads.
      while (cache.size >= 12) {
        const oldest = cache.keys().next().value!
        cache.get(oldest)!.src = ''
        cache.delete(oldest)
      }
      const image = new Image()
      image.decoding = 'async'
      image.src = url
      cache.set(url, image)
      // Decode ahead of time too; missing posters must never disrupt scrubbing.
      void image.decode().catch(() => {})
    }
  }, [urls])

  useEffect(
    () => () => {
      for (const image of images.current.values()) image.src = ''
      images.current.clear()
    },
    [],
  )
}
